import React, { useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Divider,
    Chip,
    Grid,
    Card,
    CardContent,
    Button,
    IconButton,
    CircularProgress,
    Tooltip
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    Edit as EditIcon
} from '@mui/icons-material';
import HelpIcon from "@mui/icons-material/HelpOutline";
import MainCard from "../../components/cards/MainCard";
import KeyboardArrowLeftTwoToneIcon from "@mui/icons-material/KeyboardArrowLeftTwoTone";
import { useGetDeliveryRequestByIdQuery } from '../../store/services/delivery-service';
import {
    useGetTenancyBasicsQuery,
    useLazyGetCountriesByTenancyIdQuery,
    useLazyGetDepartmentsByTenancyIdQuery,
    useLazyGetLocationsByTenancyIdQuery,
    useLazyGetCitiesByTenancyIdQuery,
    useLazyGetStatesByTenancyIdQuery,
    useLazyGetCompanyNamesByTenancyIdQuery,
} from '../../store/services/tenancy.service';

const DeliveryRequestDetail = () => {
    const { tenancyId, requestId } = useParams();
    const navigate = useNavigate();

    const {
        data: deliveryData,
        isLoading,
        error
    } = useGetDeliveryRequestByIdQuery({ tenancyId, requestId });

    // Fetch tenancy data for group name lookup
    const { data: tenancyData } = useGetTenancyBasicsQuery({
        tenancyId: tenancyId
    });

    // Lazy queries for additional data types (meta endpoints)
    const [triggerCountries, { data: countriesData, isLoading: isLoadingCountries }] = useLazyGetCountriesByTenancyIdQuery();
    const [triggerDepartments, { data: departmentsData, isLoading: isLoadingDepartments }] = useLazyGetDepartmentsByTenancyIdQuery();
    const [triggerLocations, { data: locationsData, isLoading: isLoadingLocations }] = useLazyGetLocationsByTenancyIdQuery();
    const [triggerCities, { data: citiesData, isLoading: isLoadingCities }] = useLazyGetCitiesByTenancyIdQuery();
    const [triggerStates, { data: statesData, isLoading: isLoadingStates }] = useLazyGetStatesByTenancyIdQuery();
    const [triggerCompanyNames, { data: companyNamesData, isLoading: isLoadingCompanyNames }] = useLazyGetCompanyNamesByTenancyIdQuery();

    // Check if any meta data is loading
    const isLoadingMetaData = isLoadingCountries || isLoadingDepartments || isLoadingLocations || isLoadingCities || isLoadingStates || isLoadingCompanyNames;

    // Trigger the appropriate meta endpoint query based on recipient type
    useEffect(() => {
        if (deliveryData?.items?.length > 0 && tenancyId) {
            const recipientType = deliveryData.items[0].recipientType;

            switch (recipientType) {
                case 'Country':
                    triggerCountries({ tenancyId });
                    break;
                case 'Department':
                    triggerDepartments({ tenancyId });
                    break;
                case 'Location':
                    triggerLocations({ tenancyId });
                    break;
                case 'City':
                    triggerCities({ tenancyId });
                    break;
                case 'State':
                    triggerStates({ tenancyId });
                    break;
                case 'CompanyName':
                    triggerCompanyNames({ tenancyId });
                    break;
                default:
                    break;
            }
        }
    }, [deliveryData, tenancyId, triggerCountries, triggerDepartments, triggerLocations, triggerCities, triggerStates, triggerCompanyNames]);

    // Get the appropriate meta data based on recipient type
    const getMetaData = useCallback((type) => {
        switch (type) {
            case 'Country': return countriesData;
            case 'Department': return departmentsData;
            case 'Location': return locationsData;
            case 'City': return citiesData;
            case 'State': return statesData;
            case 'CompanyName': return companyNamesData;
            default: return null;
        }
    }, [countriesData, departmentsData, locationsData, citiesData, statesData, companyNamesData]);

    const handleEdit = () => {
        navigate(`/journeybuilder/edit/${tenancyId}/${requestId}`);
    };

    // Helper function to get the appropriate display name
    const getDisplayName = (item) => {
        return item.contentName || item.contentId;
    };

    // Helper function to get content type display text
    const getContentTypeDisplay = (contentType) => {
        switch (contentType) {
            case 'RandomSimulation':
                return 'Random Simulation';
            case 'Simulation':
                return 'Simulation';
            case 'Course':
                return 'Course';
            case 'ContentPack':
                return 'Content Pack';
            default:
                return contentType;
        }
    };

    // Helper function to get recipient info with name resolution
    const getRecipientInfo = useCallback((items) => {
        if (!items || items.length === 0) {
            return { type: 'Unknown', display: 'N/A' };
        }

        const firstItem = items[0];
        const type = firstItem.recipientType || 'Tenancy';

        if (type === 'Tenancy') {
            return { type: 'Tenancy', display: 'All Users' };
        }

        // For User type, sendToId is the email
        if (type === 'User') {
            return { type: type, display: firstItem.sendToId || 'N/A' };
        }

        // For Group type (MS/Google synced groups), look up the group name from tenancy data
        if (type === 'Group' && tenancyData?.groups) {
            // Convert to string for comparison since sendToId is stored as string but userGroupId may be number
            const group = tenancyData.groups.find(g => String(g.userGroupId) === String(firstItem.sendToId));
            if (group) {
                return { type: type, display: group.name };
            }
        }

        // For other additional data types (Department, Location, City, Country, State, CompanyName)
        // Look up by ID from the corresponding meta endpoint data
        const metaTypes = ['Country', 'Department', 'Location', 'City', 'State', 'CompanyName'];
        if (metaTypes.includes(type)) {
            const metaData = getMetaData(type);
            if (metaData) {
                const item = metaData.find(m => String(m.id) === String(firstItem.sendToId));
                if (item) {
                    return { type: type, display: item.name };
                }
            }
        }

        // Fallback to sendToId if no lookup found
        return {
            type: type,
            display: firstItem.sendToId || 'N/A'
        };
    }, [tenancyData, getMetaData]);

    // Compute recipient info with memoization to re-run when data loads
    const recipientInfo = useMemo(() => getRecipientInfo(deliveryData?.items), [deliveryData?.items, getRecipientInfo]);


    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography color="error" variant="h6">
                    Error: {error.status} - {error.data?.message || 'Failed to load journey details'}
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<BackIcon />}
                    onClick={() => navigate('/journeybuilder')}
                    sx={{ mt: 2 }}
                >
                    Back
                </Button>
            </Paper>
        );
    }

    const request = deliveryData?.request;
    // Sort items by scheduledFor date in ascending order
    const items = [...(deliveryData?.items || [])].sort((a, b) => {
        return new Date(a.scheduledFor) - new Date(b.scheduledFor);
    });

    const statusColor = (status) => {
        switch (status) {
            case 'Pending': return 'warning';
            case 'InProgress': return 'secondary';
            case 'Completed': return 'primary';
            case 'Cancelled': return 'error';
            default: return 'default';
        }
    };

    const itemStatusColor = (status) => {
        switch (status) {
            case 'Pending': return 'warning';
            case 'Sent': return 'primary';
            case 'Failed': return 'error';
            case 'Cancelled': return 'error';
            default: return 'default';
        }
    };

    return (
        <MainCard
            content={true}
            boxShadow={true}
            border={false}
            sx={{ bgcolor: 'background.default' }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Button
                    onClick={() => navigate('/journeybuilder')}
                    variant="contained"
                    startIcon={<KeyboardArrowLeftTwoToneIcon />}
                >
                    Back
                </Button>
            </Box>
            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" component="div" gutterBottom>
                        Journey Name
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                        {request?.name}
                    </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" component="div" gutterBottom>
                        Status
                    </Typography>
                    <Chip
                        label={request?.status === "InProgress" ? "In Progress" : request?.status}
                        color={statusColor(request?.status)}
                        size="small"
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" component="div" gutterBottom>
                        Recipient
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                            label={recipientInfo.type === 'Tenancy' ? 'All Users' : recipientInfo.type}
                            color="secondary"
                            size="small"
                        />
                        {recipientInfo.type !== 'Tenancy' && (
                            isLoadingMetaData ? (
                                <CircularProgress size={16} />
                            ) : (
                                <Typography variant="body1">
                                    {recipientInfo.display}
                                </Typography>
                            )
                        )}
                    </Box>
                </Grid>

                {/*empty grid item for spacing*/}
                <Grid item xs={12} md={4}>

                </Grid>
                <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" component="div" gutterBottom>
                        Created At
                    </Typography>
                    <Typography variant="body1">
                        {new Date(request?.createdAt).toLocaleString('en-GB')}
                    </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" component="div" gutterBottom>
                        Last Updated
                    </Typography>
                    <Typography variant="body1">
                        {new Date(request?.updatedAt).toLocaleString('en-GB')}
                    </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" component="div" gutterBottom>
                        Created By
                    </Typography>
                    <Typography variant="body1">
                        {request?.createdById || 'N/A'}
                    </Typography>
                </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
    <Typography variant="h6" gutterBottom sx={{ mb: 0, mr: 1 }}>
        Items ({items.length})
    </Typography>
    <Tooltip title="Journeys will be delivered on the scheduled day. Exact timing may vary depending on system load and scheduling.">
        <IconButton size="small">
            <HelpIcon sx={{ color: "secondary.main" }} />
        </IconButton>
    </Tooltip>
</Box>

            {items.length === 0 ? (
                <Typography>No items in this journey.</Typography>
            ) : (
                <>
                    <Box sx={{ mb: 2 }}>
                        <Grid container spacing={2}>
                            {items.map((item, index) => (
                                <Grid item xs={12} md={6} key={item.id || index}>
                                    <MainCard
                                        content={true}
                                        boxShadow={true}
                                        border={false}
                                        sx={{ bgcolor: 'background.default' }}
                                        title={

                                            <Grid container spacing={2}>
                                                <Grid item xs={8}>
                                                    <Typography variant="subtitle1" component="div">
                                                        Item {index + 1}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={4} sx={{ textAlign: 'right' }}>
                                                    <Chip
                                                        label={item.status}
                                                        color={itemStatusColor(item.status)}
                                                        size="small"
                                                    />
                                                </Grid>
                                            </Grid>
                                        }
                                    >
                                        <Grid container spacing={2}>

                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2" component="div">
                                                    Content Name: {getDisplayName(item)}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Content Type: {getContentTypeDisplay(item.contentType)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle2" component="div">
                                                    {item.isBatchSend ? 'Start Date:' : 'Scheduled For:'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {new Date(item.scheduledFor).toLocaleDateString('en-GB')}
                                                </Typography>
                                            </Grid>
                                            {item.isBatchSend && (
                                                <>
                                                    <Grid item xs={12} sm={6}>
                                                        <Typography variant="subtitle2" component="div">
                                                            End Date:
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {item.scheduledEndDate ? new Date(item.scheduledEndDate).toLocaleDateString('en-GB') : 'N/A'}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <Typography variant="subtitle2" component="div">
                                                            Time Window:
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {item.sendingTimeStart || '00:00'} - {item.sendingTimeEnd || '23:59'}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <Typography variant="subtitle2" component="div">
                                                            Pause on Weekends:
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {item.pauseWeekends ? 'Yes' : 'No'}
                                                        </Typography>
                                                    </Grid>
                                                </>
                                            )}
                                            <Grid item xs={12}>
                                                <Chip
                                                    label={item.isBatchSend ? 'Batch Send' : 'Direct Send'}
                                                    color={item.isBatchSend ? 'secondary' : 'default'}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </Grid>
                                        </Grid>
                                    </MainCard>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </>
            )}
        </MainCard>
    );
};

export default DeliveryRequestDetail;